import { useState, useEffect, useCallback, useRef } from 'react';
import { api, LitLegionPlayerStats } from '@/lib/api';

interface UseLitLegionStatsReturn {
  stats: LitLegionPlayerStats | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useLitLegionStats(): UseLitLegionStatsReturn {
  const [stats, setStats] = useState<LitLegionPlayerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshRef = useRef(0);

  const fetchStats = useCallback(async () => {
    const id = ++refreshRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.getLitLegionStats();
      if (refreshRef.current === id) {
        setStats(result.stats);
      }
    } catch (err) {
      if (refreshRef.current === id) {
        setError(err instanceof Error ? err.message : 'Failed to load stats');
      }
    } finally {
      if (refreshRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchStats();
    return () => {
      refreshRef.current++;
    };
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats,
  };
}
